import React, { useMemo, useState } from 'react';
import Select from 'react-select';
import { NimbusModal, NimbusModalContent } from '../components/NimbusModal';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { departmentsApi, peopleApi, physicalSitesApi } from '../services/api';
import { useToast } from '../hooks/useToast';
import { useSwal } from '../hooks/useSwal';
import { PageHeader } from '../components/PageHeader';
import { NimbusTable } from '../components/NimbusTable';
import { SortableTh } from '../components/SortableTh';
import { useSortableData } from '../hooks/useSortableData';
import { usePagination } from '../hooks/usePagination';
import { Pagination } from '../components/Pagination';
import { reactSelectStyles } from '../utils/reactSelectStyles';
import { toneFromMap, type Tone } from '../constants/tone';

type PersonSortField = 'name' | 'email' | 'jobTitle' | 'department' | 'status';

const PERSON_STATUS_TONE: Record<string, Tone> = { active: 'success', inactive: 'neutral' };

type Department = { departmentId: string; name: string };

type Person = {
  personId: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  jobTitle?: string;
  departmentId?: string | null;
  department?: string | null;
  departmentName?: string | null;
  location?: string;
  manager?: string;
  status?: string;
  notes?: string;
};

export const PeoplePage: React.FC = () => {
  const qc = useQueryClient();
  const toast = useToast();
  const swal = useSwal();

  // Quick-add department inline form
  const [quickDeptOpen, setQuickDeptOpen] = useState(false);
  const [quickDeptName, setQuickDeptName] = useState('');
  const [quickDeptSaving, setQuickDeptSaving] = useState(false);

  const { data: people = [], isLoading, error } = useQuery<Person[]>({
    queryKey: ['people'],
    queryFn: peopleApi.getAll,
  });

  const { data: departments = [] } = useQuery<Department[]>({
    queryKey: ['departments'],
    queryFn: departmentsApi.getAll,
  });

  const { data: physicalSites = [] } = useQuery<any[]>({
    queryKey: ['physical-sites'],
    queryFn: physicalSitesApi.getAll,
  });

  const siteOptions = useMemo(
    () => physicalSites.map((s) => ({ value: s.siteId, label: s.name || s.siteId })),
    [physicalSites]
  );

  const deptNameById = useMemo(() => {
    const map = new Map<string, string>();
    departments.forEach((d) => map.set(d.departmentId, d.name));
    return map;
  }, [departments]);

  const [search, setSearch] = useState('');

  const searched = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return people;
    return people.filter((p) => {
      const name = `${p.firstName || ''} ${p.lastName || ''}`.trim() || p.personId;
      return name.toLowerCase().includes(q) || (p.email || '').toLowerCase().includes(q);
    });
  }, [people, search]);

  const { sorted, field: sortField, dir: sortDir, toggle: toggleSort } = useSortableData<Person, PersonSortField>(
    searched,
    (p, f) => {
      switch (f) {
        case 'name':
          return `${p.firstName || ''} ${p.lastName || ''}`.trim() || p.personId;
        case 'email':
          return p.email || '';
        case 'jobTitle':
          return p.jobTitle || '';
        case 'department':
          return (p.departmentName || (p.departmentId ? deptNameById.get(p.departmentId) : null) || p.department || '') as string;
        case 'status':
          return p.status || 'active';
        default:
          return '';
      }
    },
    'name',
  );

  const { pageItems, page, setPage, totalPages, pageSize, totalItems } = usePagination(sorted, 25);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);


  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    jobTitle: '',
    departmentId: '',
    location: '',
    manager: '',
    status: 'active',
    notes: '',
  });

  const resetForm = () => {
    setEditingId(null);
    setForm({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      jobTitle: '',
      departmentId: '',
      location: '',
      manager: '',
      status: 'active',
      notes: '',
    });
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (person: Person) => {
    beginEdit(person);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
    setQuickDeptOpen(false);
    setQuickDeptName('');
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      // Validate required fields
      if (!form.firstName?.trim()) {
        throw new Error('First Name is required');
      }
      if (!form.lastName?.trim()) {
        throw new Error('Last Name is required');
      }

      const departmentName = form.departmentId ? (deptNameById.get(form.departmentId) || form.departmentId) : null;
      const payload: any = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        phone: form.phone || null,
        jobTitle: form.jobTitle || null,
        departmentId: form.departmentId || null,
        departmentName,
        location: form.location || null,
        manager: form.manager || null,
        status: form.status || 'active',
        notes: form.notes || null,
      };

      if (editingId) {
        await peopleApi.update(editingId, payload);
        return;
      }
      await peopleApi.create(payload);
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['people'] });
      resetForm();
      setShowModal(false);
      setEditingId(null);
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to save person');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (p: Person) => {
      await peopleApi.delete(p.personId);
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['people'] });
      toast.success('Person deleted.');
    },
    onError: (e: Error) => toast.error(`Failed to delete: ${e.message}`),
  });

  const handleDelete = async (p: Person) => {
    const name = `${p.firstName || ''} ${p.lastName || ''}`.trim() || p.personId;
    const ok = await swal.confirmDelete(name);
    if (!ok) return;
    deleteMutation.mutate(p);
  };

  const beginEdit = (p: Person) => {
    setEditingId(p.personId);
    setForm({
      firstName: p.firstName || '',
      lastName: p.lastName || '',
      email: p.email || '',
      phone: p.phone || '',
      jobTitle: p.jobTitle || '',
      departmentId: (p.departmentId || p.department || '') as string,
      location: p.location || '',
      manager: p.manager || '',
      status: p.status || 'active',
      notes: p.notes || '',
    });
  };

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">
      <PageHeader
        icon="ri-team-line"
        title="People"
        colour="success"
        actions={
          <button className="np-btn np-btn-primary" onClick={openCreateModal}>
            <i className="ri-add-line"></i>Create Person
          </button>
        }
      />

      <div className="np-card">
        <div className="np-card-body border-bottom py-3">
          <div className="d-flex gap-2 flex-wrap align-items-center">
            <div className="np-search" style={{ maxWidth: 260 }}>
              <i className="ri-search-line"></i>
              <input
                type="text"
                placeholder="Search name, email…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && (
                <button className="np-icon-btn" onClick={() => setSearch('')}>
                  <i className="ri-close-line"></i>
                </button>
              )}
            </div>
            {search && (
              <button className="np-btn np-btn-ghost np-btn-sm" onClick={() => setSearch('')}>
                <i className="ri-filter-off-line"></i>Clear
              </button>
            )}
            <span className="np-sub np-mb-0 ms-auto">{sorted.length} result{sorted.length !== 1 ? 's' : ''}</span>
          </div>
        </div>
        <div className="np-card-body">
          {isLoading ? (
            <div className="d-flex justify-content-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : error ? (
            <div className="np-banner np-tone-danger mb-0"><i className="ri-error-warning-line"></i>Failed to load people.</div>
          ) : sorted.length === 0 ? (
            <div className="np-sub">{people.length === 0 ? 'No people found.' : 'No results match your filters.'}</div>
          ) : (
            <NimbusTable thead={<tr>
                    <SortableTh field="name" active={sortField === 'name'} dir={sortDir} onSort={toggleSort}>Name</SortableTh>
                    <SortableTh field="email" active={sortField === 'email'} dir={sortDir} onSort={toggleSort}>Email</SortableTh>
                    <SortableTh field="jobTitle" active={sortField === 'jobTitle'} dir={sortDir} onSort={toggleSort} width="220px">Job Title</SortableTh>
                    <SortableTh field="department" active={sortField === 'department'} dir={sortDir} onSort={toggleSort} width="220px">Department</SortableTh>
                    <SortableTh field="status" active={sortField === 'status'} dir={sortDir} onSort={toggleSort} width="160px">Status</SortableTh>
                    <th style={{ width: '180px' }}></th>
                  </tr>}>
                  {pageItems.map((p) => {
                    const name = `${p.firstName || ''} ${p.lastName || ''}`.trim() || p.personId;
                    const dept = (p.departmentName || (p.departmentId ? deptNameById.get(p.departmentId) : null) || p.department || '-') as any;

                    return (
                      <tr key={p.personId}>
                        <td className="np-row-title-static">{name}</td>
                        <td className="np-sub">{p.email || '-'}</td>
                        <td>{p.jobTitle || '-'}</td>
                        <td>{dept || '-'}</td>
                        <td><span className={`np-chip np-tone-${toneFromMap(PERSON_STATUS_TONE, p.status || 'active', 'neutral')}`}>{p.status || 'active'}</span></td>
                        <td>
                          <div className="d-flex gap-1">
                            <button
                              className="np-icon-btn"
                              onClick={() => openEditModal(p)}
                              title="Edit"
                            >
                              <i className="ri-edit-line"></i>
                            </button>
                            <button
                              className="np-icon-btn np-icon-btn-danger"
                              onClick={() => handleDelete(p)}
                              disabled={deleteMutation.isPending}
                              title="Delete"
                            >
                              <i className="ri-delete-bin-line"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
            </NimbusTable>
          )}
          {pageItems.length > 0 && (
            <Pagination page={page} totalPages={totalPages} pageSize={pageSize} totalItems={totalItems} onPageChange={setPage} />
          )}
        </div>
      </div>

      {showModal && (
        <NimbusModal>
          <NimbusModalContent
            title={editingId ? 'Edit Person' : 'Add Person'}
            onClose={closeModal}
            footer={
              <>
                <button type="button" className="np-btn np-btn-ghost" onClick={closeModal}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="np-btn np-btn-primary"
                  onClick={() => { saveMutation.mutate(); closeModal(); }}
                  disabled={saveMutation.isPending || !form.firstName.trim() || !form.lastName.trim()}
                >
                  {saveMutation.isPending ? 'Saving…' : editingId ? 'Save Changes' : 'Create Person'}
                </button>
              </>
            }
          >
                <div className="row g-3">
                  <div className="col-12 col-md-6 np-field">
                    <label>First Name *</label>
                    <input className="np-input" value={form.firstName} onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))} required />
                  </div>
                  <div className="col-12 col-md-6 np-field">
                    <label>Last Name *</label>
                    <input className="np-input" value={form.lastName} onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))} required />
                  </div>
                  <div className="col-12 col-md-6 np-field">
                    <label>Email *</label>
                    <input className="np-input" type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
                  </div>
                  <div className="col-12 col-md-6 np-field">
                    <label>Phone</label>
                    <input className="np-input" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
                  </div>
                  <div className="col-12 col-md-6 np-field">
                    <label>Job Title</label>
                    <input className="np-input" value={form.jobTitle} onChange={(e) => setForm((f) => ({ ...f, jobTitle: e.target.value }))} />
                  </div>
                  <div className="col-12 col-md-6 np-field">
                    <div className="d-flex align-items-center justify-content-between mb-1">
                      <label className="mb-0">Department</label>
                      <button
                        type="button"
                        className="np-btn np-btn-ghost np-btn-sm p-0"
                        style={{ fontSize: '0.75rem' }}
                        onClick={() => { setQuickDeptOpen(o => !o); setQuickDeptName(''); }}
                      >
                        {quickDeptOpen ? 'Cancel' : <><i className="ri-add-line" /> Add department</>}
                      </button>
                    </div>
                    <Select
                      options={departments.map((d) => ({ value: d.departmentId, label: d.name }))}
                      value={form.departmentId ? { value: form.departmentId, label: deptNameById.get(form.departmentId) ?? form.departmentId } : null}
                      onChange={(opt) => setForm((f) => ({ ...f, departmentId: opt?.value ?? '' }))}
                      placeholder="Select department…"
                      isClearable
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      styles={reactSelectStyles()}
                    />
                    {quickDeptOpen && (
                      <div className="input-group mt-2">
                        <input
                          type="text"
                          className="np-input"
                          placeholder="Department name"
                          value={quickDeptName}
                          autoFocus
                          onChange={e => setQuickDeptName(e.target.value)}
                          onKeyDown={async e => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              if (!quickDeptName.trim() || quickDeptSaving) return;
                              setQuickDeptSaving(true);
                              try {
                                const res = await departmentsApi.create({ name: quickDeptName.trim() });
                                await qc.invalidateQueries({ queryKey: ['departments'] });
                                setForm(f => ({ ...f, departmentId: res.departmentId ?? res.department?.departmentId ?? '' }));
                                setQuickDeptOpen(false);
                                setQuickDeptName('');
                                toast.success(`Department "${quickDeptName.trim()}" created`);
                              } catch {
                                toast.error('Failed to create department');
                              } finally {
                                setQuickDeptSaving(false);
                              }
                            }
                          }}
                        />
                        <button
                          type="button"
                          className="np-btn np-btn-primary np-btn-sm"
                          disabled={!quickDeptName.trim() || quickDeptSaving}
                          onClick={async () => {
                            if (!quickDeptName.trim() || quickDeptSaving) return;
                            setQuickDeptSaving(true);
                            try {
                              const res = await departmentsApi.create({ name: quickDeptName.trim() });
                              await qc.invalidateQueries({ queryKey: ['departments'] });
                              setForm(f => ({ ...f, departmentId: res.departmentId ?? res.department?.departmentId ?? '' }));
                              setQuickDeptOpen(false);
                              setQuickDeptName('');
                              toast.success(`Department "${quickDeptName.trim()}" created`);
                            } catch {
                              toast.error('Failed to create department');
                            } finally {
                              setQuickDeptSaving(false);
                            }
                          }}
                        >
                          {quickDeptSaving ? <span className="spinner-border spinner-border-sm" /> : 'Save'}
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="col-12 col-md-6 np-field">
                    <label>Location</label>
                    <Select
                      options={siteOptions}
                      value={form.location ? (siteOptions.find((s) => s.label === form.location) ?? { value: form.location, label: form.location }) : null}
                      onChange={(opt) => setForm((f) => ({ ...f, location: opt?.label ?? '' }))}
                      placeholder="Select location…"
                      isClearable
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      styles={reactSelectStyles()}
                    />
                  </div>
                  <div className="col-12 col-md-6 np-field">
                    <label>Manager</label>
                    <Select
                      options={people
                        .filter((p) => !editingId || p.personId !== editingId)
                        .map((p) => ({ value: p.personId, label: [p.firstName, p.lastName].filter(Boolean).join(' ') || p.personId }))}
                      value={form.manager ? { value: form.manager, label: (() => { const p = people.find((x) => x.personId === form.manager); return p ? [p.firstName, p.lastName].filter(Boolean).join(' ') || p.personId : form.manager; })() } : null}
                      onChange={(opt) => setForm((f) => ({ ...f, manager: opt?.value ?? '' }))}
                      placeholder="Select manager…"
                      isClearable
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      styles={reactSelectStyles()}
                    />
                  </div>
                  <div className="col-12 np-field">
                    <label>Status</label>
                    <select className="np-select" value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}>
                      <option value="active">active</option>
                      <option value="inactive">inactive</option>
                    </select>
                  </div>
                  <div className="col-12 np-field">
                    <label>Notes</label>
                    <textarea className="np-textarea" rows={2} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
                  </div>
                </div>
          </NimbusModalContent>
        </NimbusModal>
      )}
    </div>
  );
};
